import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";
import { getAccessToken } from "../../../storage/storage";
import { FormatDate } from "../../../utils/FormateDate";
import { getApiErrorMessage as extractApiErrorMessage } from "../../../utils/apiError";

const COLORS = {
  primary: "#586989",
  cta: "#FFF5CE",
  warnBg: "#fff8e1",
  warnBorder: "#f59e0b",
  warnText: "#92400e",
  warnBody: "#78350f",
};

const cleanerDisplayName = (c) => {
  if (!c) return "";
  const firstLast = [c.first_name, c.surname || c.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (firstLast) return firstLast;
  return (
    c.name ||
    c.full_name ||
    c.cleaner_name ||
    (c.id != null ? `Cleaner #${c.id}` : "")
  );
};

export default function AssignCleanerModal({
  open,
  onClose,
  selectedStays,
  propertyId,
  propertyName,
  onAssigned,
}) {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [cleaners, setCleaners] = useState([]);
  const [cleanersLoading, setCleanersLoading] = useState(false);
  const [cleanersError, setCleanersError] = useState(null);
  const [selectedCleanerId, setSelectedCleanerId] = useState("");
  const [reassignAcknowledged, setReassignAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedCleanerId("");
    setReassignAcknowledged(false);
    setCleanersError(null);
  }, [open]);

  useEffect(() => {
    if (!open || !propertyId) return;
    setCleanersLoading(true);
    // Use the same endpoint as single-stay assignment so the dropdown returns
    // Cleaner objects (Cleaner.id + Cleaner-model names). This keeps the
    // selected id in the same space as stay.cleaner and the name casing
    // consistent with the stay rows, and matches what bulk-assign expects.
    axios
      .get(`${BASE_URL}/api/cleaners-by-property/?property_id=${propertyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        setCleaners(list);
      })
      .catch((err) => {
        setCleanersError(
          extractApiErrorMessage(err, "Couldn't load cleaners. Please try again.")
        );
      })
      .finally(() => setCleanersLoading(false));
  }, [open, propertyId, BASE_URL, token]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, submitting]);

  const classifyStay = (stay, newCleanerId) => {
    const existingId =
      stay?.cleaner_id ?? stay?.cleaner?.id ?? stay?.cleaner ?? null;
    const status = String(stay?.status || "").toUpperCase();
    // Surface ANY stay that already has a cleaner — whether it's the same
    // cleaner being selected or a different one — so the owner can see the
    // current state (pending / accepted) before confirming. `sameCleaner`
    // lets the UI word it correctly: re-assigning to the same cleaner keeps
    // the stay's status, while a different cleaner is a destructive reassign.
    const hasExistingAssignment = !!stay?.cleaner_name && existingId != null;
    if (!hasExistingAssignment) return { kind: "unassigned", sameCleaner: false };
    const sameCleaner =
      newCleanerId != null && String(existingId) === String(newCleanerId);
    if (status === "ACCEPTED") return { kind: "accepted", sameCleaner };
    if (status === "PENDING") return { kind: "pending", sameCleaner };
    return { kind: "other", sameCleaner };
  };

  // Partition the selected stays relative to the chosen cleaner:
  //  • reassign* — held by a DIFFERENT cleaner → destructive reassign (needs ack)
  //  • skipped*  — already held by THIS cleaner → left untouched (backend skips)
  //  • unassigned — no cleaner → fresh assignment
  const {
    reassignAccepted,
    reassignPending,
    reassignOther,
    skippedAccepted,
    skippedPending,
    skippedOther,
    unassignedStays,
  } = useMemo(() => {
    const out = {
      reassignAccepted: [],
      reassignPending: [],
      reassignOther: [],
      skippedAccepted: [],
      skippedPending: [],
      skippedOther: [],
      unassignedStays: [],
    };
    if (!selectedCleanerId) return out;
    for (const s of selectedStays) {
      const { kind, sameCleaner } = classifyStay(s, selectedCleanerId);
      if (kind === "unassigned") {
        out.unassignedStays.push(s);
      } else if (sameCleaner) {
        if (kind === "accepted") out.skippedAccepted.push(s);
        else if (kind === "pending") out.skippedPending.push(s);
        else out.skippedOther.push(s);
      } else {
        if (kind === "accepted") out.reassignAccepted.push(s);
        else if (kind === "pending") out.reassignPending.push(s);
        else out.reassignOther.push(s);
      }
    }
    return out;
  }, [selectedStays, selectedCleanerId]);

  // "Conflicts" now means ONLY destructive reassignments from another cleaner.
  const conflicts = useMemo(
    () => [...reassignAccepted, ...reassignPending, ...reassignOther],
    [reassignAccepted, reassignPending, reassignOther]
  );
  const skippedStays = useMemo(
    () => [...skippedAccepted, ...skippedPending, ...skippedOther],
    [skippedAccepted, skippedPending, skippedOther]
  );
  const conflictCount = conflicts.length;
  const acceptedCount = reassignAccepted.length;
  const pendingCount = reassignPending.length;
  const skippedCount = skippedStays.length;
  const skippedAcceptedCount = skippedAccepted.length;
  const skippedPendingCount = skippedPending.length;
  const unassignedCount = unassignedStays.length;
  // Stays that will actually be (re)assigned on confirm.
  const willAssignCount = unassignedCount + conflictCount;

  const existingCleanerNames = useMemo(
    () =>
      Array.from(
        new Set(conflicts.map((s) => s.cleaner_name).filter(Boolean))
      ),
    [conflicts]
  );

  const newCleanerName = cleanerDisplayName(
    cleaners.find(
      (c) => String(c.id ?? c.cleaner_id) === String(selectedCleanerId)
    )
  );

  const noEligibleCleaners =
    !cleanersLoading && !cleanersError && cleaners.length === 0;

  const canConfirm =
    !submitting &&
    !!selectedCleanerId &&
    !noEligibleCleaners &&
    willAssignCount > 0 &&
    (conflictCount === 0 || reassignAcknowledged);

  const warningSeverity = acceptedCount > 0 ? "high" : "medium";

  // Headline for the destructive-reassign warning (different cleaner).
  const warningHeadline = (() => {
    const name =
      existingCleanerNames.length === 1
        ? existingCleanerNames[0]
        : "another cleaner";
    if (acceptedCount > 0 && pendingCount > 0) {
      return `${conflictCount} stay${
        conflictCount === 1 ? "" : "s"
      } will be reassigned from ${name} (${acceptedCount} accepted, ${pendingCount} pending)`;
    }
    if (acceptedCount > 0) {
      return `${acceptedCount} stay${
        acceptedCount === 1 ? "" : "s"
      } already accepted by ${name} will be reassigned`;
    }
    return `${conflictCount} stay${
      conflictCount === 1 ? "" : "s"
    } will be reassigned from ${name}`;
  })();

  // Group reassign conflicts by current cleaner_name, with per-stay status so we
  // can surface accepted stays inline as red-tinted markers next to the ID.
  const conflictGroups = useMemo(() => {
    if (!selectedCleanerId) return [];
    const map = new Map();
    for (const s of conflicts) {
      const name = s.cleaner_name || "another cleaner";
      if (!map.has(name)) {
        map.set(name, { cleanerName: name, items: [] });
      }
      const { kind } = classifyStay(s, selectedCleanerId);
      map.get(name).items.push({ id: s.id, kind });
    }
    return Array.from(map.values());
  }, [conflicts, selectedCleanerId]);

  const acknowledgeLabel = (() => {
    const name =
      existingCleanerNames.length === 1
        ? existingCleanerNames[0]
        : "another cleaner";
    if (acceptedCount > 0) {
      return `I understand ${conflictCount} stay${
        conflictCount === 1 ? "" : "s"
      } will be reassigned from ${name} (${acceptedCount} already accepted)`;
    }
    return `I understand ${conflictCount} stay${
      conflictCount === 1 ? "" : "s"
    } will be reassigned from ${name}`;
  })();

  // Informational headline for stays already with the selected cleaner.
  const skippedHeadline = (() => {
    const name = newCleanerName || "this cleaner";
    if (skippedAcceptedCount > 0 && skippedPendingCount > 0) {
      return `${skippedCount} stay${
        skippedCount === 1 ? "" : "s"
      } already assigned to ${name} (${skippedAcceptedCount} accepted, ${skippedPendingCount} pending)`;
    }
    return `${skippedCount} stay${
      skippedCount === 1 ? "" : "s"
    } already assigned to ${name}`;
  })();

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/api/stays/bulk-assign/`,
        {
          cleaner_id: parseInt(selectedCleanerId, 10),
          stay_ids: selectedStays.map((s) => s.id),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data || {};
      const results = Array.isArray(data.results) ? data.results : [];
      const knownStatuses = new Set(["assigned", "conflict"]);
      const otherFailed = results.filter(
        (r) => r?.status && !knownStatuses.has(String(r.status).toLowerCase())
      );

      const cleanerLabel = newCleanerName || "the cleaner";

      // Resolve the counts to display. The newer backend returns a
      // `skipped_count` field and excludes already-with-this-cleaner stays from
      // `assigned_count`. An older backend (pre-skip) lumps those into
      // `assigned_count`, which over-reports. When the skip field is absent we
      // fall back to the modal's own classification so the toast always matches
      // what the user reviewed (assigned + reassigned, skipped left unchanged).
      const backendHasSkip =
        data.skipped_count !== undefined && data.skipped_count !== null;

      const freshCount = backendHasSkip
        ? data.assigned_count ?? unassignedCount
        : unassignedCount;
      const reassignedCount = backendHasSkip
        ? data.conflict_count ?? conflictCount
        : conflictCount;
      const skippedShown = backendHasSkip
        ? data.skipped_count ?? skippedCount
        : skippedCount;
      const actionedCount = freshCount + reassignedCount;

      if (actionedCount > 0) {
        let msg = `${actionedCount} stay${
          actionedCount === 1 ? "" : "s"
        } assigned to ${cleanerLabel}`;
        if (reassignedCount > 0) {
          msg += ` · ${reassignedCount} reassigned from another cleaner`;
        }
        toast.success(msg);
        if (skippedShown > 0) {
          toast(
            `${skippedShown} stay${
              skippedShown === 1 ? "" : "s"
            } already with ${cleanerLabel} — left unchanged`
          );
        }
      }

      if (otherFailed.length > 0) {
        const firstMessage =
          otherFailed[0]?.message ||
          `${otherFailed.length} stay${
            otherFailed.length === 1 ? "" : "s"
          } could not be processed`;
        toast.error(firstMessage);
      } else if (actionedCount === 0) {
        if (skippedShown > 0) {
          toast(
            `All ${skippedShown} stay${
              skippedShown === 1 ? "" : "s"
            } already assigned to ${cleanerLabel}.`
          );
        } else {
          toast.error("No stays were assigned. Please try again.");
        }
      }

      onAssigned?.(data);
      onClose();
    } catch (err) {
      toast.error(
        extractApiErrorMessage(err, "Couldn't assign cleaner. Please try again."),
        { duration: 6000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Assign cleaner to ${selectedStays.length} stays`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2
              className="text-base font-bold"
              style={{ color: COLORS.primary }}
            >
              Assign cleaner to {selectedStays.length} stay
              {selectedStays.length === 1 ? "" : "s"}
            </h2>
            {propertyName && (
              <p className="text-xs text-gray-500 mt-0.5">{propertyName}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <FaTimes />
          </button>
        </div>

        <div className="mt-4">
          <label
            className="block text-xs font-semibold mb-1"
            style={{ color: COLORS.primary }}
          >
            Select cleaner
          </label>
          {cleanersLoading ? (
            <div className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-500">
              Loading cleaners…
            </div>
          ) : cleanersError ? (
            <div className="border border-red-300 bg-red-50 rounded-md px-3 py-2 text-sm text-red-700">
              {cleanersError}
            </div>
          ) : noEligibleCleaners ? (
            <div className="border border-amber-300 bg-amber-50 rounded-md px-3 py-2 text-sm text-amber-800">
              No cleaners have accepted an invitation for this property.
              Invite a cleaner first.
            </div>
          ) : (
            <select
              value={selectedCleanerId}
              onChange={(e) => {
                setSelectedCleanerId(e.target.value);
                setReassignAcknowledged(false);
              }}
              disabled={submitting}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#586989]"
            >
              <option value="">— Choose a cleaner —</option>
              {cleaners.map((c) => {
                const id = c.id ?? c.cleaner_id;
                return (
                  <option key={id} value={id}>
                    {cleanerDisplayName(c)}
                  </option>
                );
              })}
            </select>
          )}
        </div>

        {conflictCount > 0 && (
          <>
            <div
              className="mt-4 rounded-md border-2 px-3 py-2"
              style={
                warningSeverity === "high"
                  ? {
                      background: "#fef2f2",
                      borderColor: "#dc2626",
                    }
                  : {
                      background: COLORS.warnBg,
                      borderColor: COLORS.warnBorder,
                    }
              }
            >
              <div
                className="flex items-center gap-2 text-xs font-bold"
                style={{
                  color: warningSeverity === "high" ? "#991b1b" : COLORS.warnText,
                }}
              >
                <FaExclamationTriangle />
                {warningHeadline}
              </div>
              <ul
                className="text-xs mt-2 leading-relaxed space-y-1 list-disc pl-4"
                style={{
                  color: warningSeverity === "high" ? "#7f1d1d" : COLORS.warnBody,
                }}
              >
                {conflictGroups.map((group) => {
                  const ids = group.items;
                  const plural = ids.length !== 1;
                  return (
                    <li key={group.cleanerName}>
                      Stay{plural ? "s" : ""}{" "}
                      {ids.map((it, i) => (
                        <span key={it.id}>
                          <span className="font-semibold">#{it.id}</span>
                          {it.kind === "accepted" && (
                            <span
                              className="ml-0.5 font-semibold"
                              style={{ color: "#991b1b" }}
                            >
                              {" "}
                              (accepted)
                            </span>
                          )}
                          {i < ids.length - 2
                            ? ", "
                            : i === ids.length - 2
                            ? " and "
                            : ""}
                        </span>
                      ))}{" "}
                      {plural ? "are" : "is"} assigned to{" "}
                      <span className="font-semibold">{group.cleanerName}</span>
                      .
                    </li>
                  );
                })}
              </ul>
              <p
                className="text-xs mt-2 leading-relaxed"
                style={{
                  color: warningSeverity === "high" ? "#7f1d1d" : COLORS.warnBody,
                }}
              >
                Confirming will unassign{" "}
                <span className="font-semibold">
                  {existingCleanerNames.length === 1
                    ? existingCleanerNames[0]
                    : "the current cleaners"}
                </span>{" "}
                and assign{" "}
                <span className="font-semibold">
                  {newCleanerName || "the new cleaner"}
                </span>{" "}
                to those stays.
              </p>
            </div>

            <label className="flex items-start gap-2 mt-3 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={reassignAcknowledged}
                onChange={(e) => setReassignAcknowledged(e.target.checked)}
                className="mt-0.5 accent-[#586989]"
                disabled={submitting}
              />
              <span>{acknowledgeLabel}</span>
            </label>
          </>
        )}

        {selectedCleanerId && skippedCount > 0 && (
          <div
            className="mt-4 rounded-md border px-3 py-2"
            style={{ background: "#eff6ff", borderColor: "#bfdbfe" }}
          >
            <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "#1e40af" }}>
              {skippedHeadline}
            </div>
            <p className="text-xs mt-1 leading-relaxed" style={{ color: "#1e3a8a" }}>
              {skippedCount === 1 ? "This stay is" : "These stays are"} already
              assigned to{" "}
              <span className="font-semibold">{newCleanerName || "this cleaner"}</span>{" "}
              and will be left unchanged.
              {willAssignCount > 0
                ? ` Only the ${willAssignCount} remaining stay${
                    willAssignCount === 1 ? "" : "s"
                  } will be assigned.`
                : ""}
            </p>
          </div>
        )}

        {selectedCleanerId && willAssignCount === 0 && skippedCount > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            Nothing to assign — every selected stay is already with this cleaner.
          </p>
        )}

        <div className="mt-4">
          <div
            className="text-xs font-semibold mb-1"
            style={{ color: COLORS.primary }}
          >
            Selected stays
          </div>
          <div className="border border-gray-200 bg-gray-50 rounded-md max-h-40 overflow-y-auto">
            {selectedStays.map((s) => {
              const info = selectedCleanerId
                ? classifyStay(s, selectedCleanerId)
                : { kind: "neutral", sameCleaner: false };
              const { kind, sameCleaner } = info;
              let tag = null;
              if (kind === "unassigned" && selectedCleanerId) {
                // Fresh assignment.
                tag = {
                  text: "Will be assigned",
                  bg: "#dcfce7",
                  color: "#166534",
                };
              } else if (sameCleaner) {
                // Already with the selected cleaner → left unchanged (skipped).
                tag = {
                  text:
                    kind === "accepted"
                      ? `Already accepted by ${s.cleaner_name} — kept`
                      : `Already assigned to ${s.cleaner_name} — kept`,
                  bg: "#e0f2fe",
                  color: "#075985",
                };
              } else if (kind === "accepted") {
                tag = {
                  text: `Accepted by ${s.cleaner_name} — will be reassigned`,
                  bg: "#fee2e2",
                  color: "#991b1b",
                };
              } else if (kind === "pending") {
                tag = {
                  text: `Pending with ${s.cleaner_name} — will be reassigned`,
                  bg: "#fef3c7",
                  color: "#92400e",
                };
              } else if (kind === "other") {
                tag = {
                  text: `Reassigning from ${s.cleaner_name}`,
                  bg: "#fef3c7",
                  color: "#92400e",
                };
              }

              return (
                <div
                  key={s.id}
                  className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-gray-100 last:border-b-0 text-xs"
                >
                  <span
                    className="font-semibold flex-shrink-0"
                    style={{ color: COLORS.primary }}
                  >
                    #{s.id}
                  </span>
                  <span className="text-gray-500 truncate flex-1">
                    {s.lead_guest_name && (
                      <span className="text-gray-700 font-medium">
                        {s.lead_guest_name} ·{" "}
                      </span>
                    )}
                    {FormatDate(s.check_in_date)} – {FormatDate(s.check_out_date)}
                    {s.number_of_guests != null
                      ? ` · ${s.number_of_guests} guest${s.number_of_guests !== 1 ? "s" : ""}`
                      : ""}
                    {s.number_of_pets > 0
                      ? ` · ${s.number_of_pets} pet${s.number_of_pets !== 1 ? "s" : ""}`
                      : ""}
                  </span>
                  {tag && (
                    <span
                      className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium"
                      style={{ background: tag.bg, color: tag.color }}
                    >
                      {tag.text}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-md border text-sm disabled:opacity-50"
            style={{ color: COLORS.primary, borderColor: COLORS.primary }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canConfirm}
            className="flex-1 px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50"
            style={{ background: COLORS.primary, color: "white" }}
          >
            {submitting ? "Assigning…" : "Confirm assignment"}
          </button>
        </div>
      </div>
    </div>
  );
}
